import type { IconName } from '@hotjar/hotsauce';
import type { ClickedType } from '@hotjar/global-filters/src/components/StatelessComponent/SingleFilter/ClickedFilter/ClickedFilterType';
import type { ErrorType } from '@hotjar/global-filters/src/components/StatelessComponent/SingleFilter/ErrorFilter/ErrorFilterType';
import type { FunnelType } from '@hotjar/global-filters/src/components/StatelessComponent/SingleFilter/FunnelFilter/FunnelFilterType';
import type { BROWSER_TYPE } from '../../../components/StatelessComponent/SingleFilter/BrowserFilter/BrowserFilterType';
import type {
  DateRange,
  MinimalDateConstraint,
} from '../../../components/StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import type { DEVICE_TYPE } from '../../../components/StatelessComponent/SingleFilter/DeviceTypeFilter/DeviceFilterType';
import type { FEEDBACK_TYPE } from '../../../components/StatelessComponent/SingleFilter/FeedbackFilter/FeedbackFilterType';
import type {
  LargeListFilterValue,
  BasicListOption,
} from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericLargeListFilter/GenericLargeListType';
import type { RadioDurationFilterValue } from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioDurationFilter/GenericRadioDurationType';
import type { RadioNumberFilterValue } from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioNumberFilter/GenericRadioNumberType';
import type { RadioTextFilterValue } from '../../../components/StatelessComponent/SingleFilter/GenericBloc/GenericRadioTextFilter/GenericRadioTextType';
import type { ExperimentType } from '../../../components/StatelessComponent/SingleFilter/ExperimentFilter/ExperimentFilterType';
import type { NEW_RETURNING_USER_TYPE } from '../../../components/StatelessComponent/SingleFilter/NewReturningUserFilter/NewReturningUserFilterType';
import type { NPS_TYPE } from '../../../components/StatelessComponent/SingleFilter/NPSFilter/NPSFilterType';
import type { OS_TYPE } from '../../../components/StatelessComponent/SingleFilter/OperatingSystemFilter/OperatingSystemType';
import type { RAGE_CLICK_TYPE } from '../../../components/StatelessComponent/SingleFilter/RageClickFilter/RageClickFilterType';
import type {
  UserAttributeType,
  UserAttributeOptionType,
} from '../../../components/StatelessComponent/SingleFilter/UserAttributeFilter/UserAttributeFilterType';
import type { SURVEY_COMPLETION_TYPE } from '../../../components/StatelessComponent/SingleFilter/SurveyCompletionFilter/SurveyCompletionFilterType';
import type { U_TURN_TYPE } from '../../../components/StatelessComponent/SingleFilter/UTurnFilter/UTurnFilterType';
import type { TEAM_COMMENT_TYPE } from '../../../components/StatelessComponent/SingleFilter/TeamCommentFilter/TeamCommentFilterType';
import type { Experiment } from '../../experiments/model/Experiment';

// The value of those keys are important because they are also used in:
// - the backend to specify the available features per plan
// - upsell service to show the proper modal
export enum FilterKindEnum {
  Clicked = 'clicked',
  NewReturningUserKind = 'new_returning_user',
  RageClickKind = 'rage_click',
  UTurnKind = 'u_turn',
  TeamComment = 'team_comment',
  DeviceType = 'device',
  OS = 'os',
  Country = 'country',
  Event = 'events',
  GoogleOptimize = 'google_optimize',
  Optimizely = 'optimizely',
  Unbounce = 'unbounce',
  UserAttribute = 'user_attribute',
  VisitedURL = 'visited_url',
  LandingURL = 'landing_url',
  ExitURL = 'exit_url',
  ReferrerURL = 'referrer_url',
  PageURL = 'page_url',
  Browser = 'browser',
  IncomingFeedback = 'incoming_feedback',
  SurveyCompletionKind = 'survey_completion',
  NPS = 'nps',
  UserID = 'user_id',
  Keyword = 'keyword',
  PageCount = 'page_count',
  SessionDuration = 'session_duration',
  Error = 'error',
  Funnel = 'funnel',
}

export enum FilterCategoryEnum {
  Session = 'Session',
  Behavior = 'Behavior',
  Technology = 'Technology',
  Path = 'Path',
  UserAttributes = 'User attributes',
  Feedback = 'Feedback',
  Experiment = 'Experiment',
}

export enum UpsellFooterEnum {
  UnlockPlusFilter = 'UnlockPlusFilter',
  UnlockBusinessFilter = 'UnlockBusinessFilter',
  UnlockScaleFilter = 'UnlockScaleFilter',
}

interface AbstractSingleFilterConfig<T extends FilterKindEnum> {
  kind: T;
  isLocked?: boolean;
  dateConstraint?: MinimalDateConstraint;
}

type BasicSingleFilterConfig = AbstractSingleFilterConfig<
  Exclude<
    FilterKindEnum,
    [
      FilterKindEnum.Country,
      FilterKindEnum.Event,
      FilterKindEnum.GoogleOptimize,
      FilterKindEnum.Optimizely,
      FilterKindEnum.Unbounce,
      FilterKindEnum.UserAttribute,
    ]
  >
>;

interface LargeListSingleFilterConfig
  extends AbstractSingleFilterConfig<FilterKindEnum.Country | FilterKindEnum.Event> {
  optionList?: readonly BasicListOption<string>[];
  // We want to load more option when the dropdown opens
  optionListIsLoading?: boolean;
  onDropdownShown?: () => void;
}

interface ExperimentSingleFilterConfig
  extends AbstractSingleFilterConfig<FilterKindEnum.GoogleOptimize | FilterKindEnum.Optimizely> {
  optionList?: Experiment[];
  // We want to load more option when the dropdown open
  optionListIsLoading: boolean;
  onDropdownShown: () => void;
  isIntegrationActive: boolean;
}

interface UserAttributeSingleFilterConfig
  extends AbstractSingleFilterConfig<FilterKindEnum.UserAttribute> {
  userAttributeOptionList: UserAttributeOptionType[];
  userAttributeOptionIsLoading: boolean;
  onDropdownShown: () => void;
  onAddMoreUserAttribute?: () => void;
}

export type SingleFilterConfig =
  | BasicSingleFilterConfig
  | LargeListSingleFilterConfig
  | ExperimentSingleFilterConfig
  | UserAttributeSingleFilterConfig;

export interface FilterChainConfig {
  actionableFilterList: SingleFilterConfig[];
  showSaveSegmentButton?: boolean;
  hideAllDateOption?: boolean;
  hideAllFilters?: boolean;
  hideDateRangeFilter?: boolean;
  hideAddFilters?: boolean;
  hideClearAllFilters?: boolean;
  usePrimaryStyledAddFilters?: boolean;
  useToggleGroupDatePicker?: boolean;
  dateConstraint?: MinimalDateConstraint;
  showUpsellFooter?: UpsellFooterEnum;
  isLoading?: boolean;
  hasFilterSavePermission?: boolean;
  isFunnelStep?: boolean;
}

export interface FilterMetadata {
  filterName: string;
  iconName: IconName;
  categoryName: FilterCategoryEnum;
}

interface AbstractJsonFilter<T> {
  id: string;
  kind: FilterKindEnum;
  value?: T;
}

interface NewReturningUserFilter extends AbstractJsonFilter<NEW_RETURNING_USER_TYPE> {
  kind: FilterKindEnum.NewReturningUserKind;
}

interface RageClickFilter extends AbstractJsonFilter<RAGE_CLICK_TYPE> {
  kind: FilterKindEnum.RageClickKind;
}

interface UTurnFilter extends AbstractJsonFilter<U_TURN_TYPE> {
  kind: FilterKindEnum.UTurnKind;
}

interface TeamCommentFilter extends AbstractJsonFilter<TEAM_COMMENT_TYPE> {
  kind: FilterKindEnum.TeamComment;
}

export interface DeviceFilter extends AbstractJsonFilter<DEVICE_TYPE[]> {
  kind: FilterKindEnum.DeviceType;
}

interface OSFilter extends AbstractJsonFilter<OS_TYPE[]> {
  kind: FilterKindEnum.OS;
}

export interface CountryFilter extends AbstractJsonFilter<LargeListFilterValue<string>> {
  kind: FilterKindEnum.Country;
}

export interface EventFilter extends AbstractJsonFilter<LargeListFilterValue<string>> {
  kind: FilterKindEnum.Event;
}

interface GoogleOptimizeFilter extends AbstractJsonFilter<ExperimentType> {
  kind: FilterKindEnum.GoogleOptimize;
}

interface OptimizelyFilter extends AbstractJsonFilter<ExperimentType> {
  kind: FilterKindEnum.Optimizely;
}

interface UnbounceFilter extends AbstractJsonFilter<ExperimentType> {
  kind: FilterKindEnum.Unbounce;
}

export interface UserAttributeFilter extends AbstractJsonFilter<UserAttributeType> {
  kind: FilterKindEnum.UserAttribute;
}

export interface VisitedURLFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.VisitedURL;
}

export interface LandingURLFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.LandingURL;
}

export interface ExitURLFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.ExitURL;
}

interface ReferrerURLFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.ReferrerURL;
}

interface PageURLFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.PageURL;
}

interface BrowserFilter extends AbstractJsonFilter<BROWSER_TYPE[]> {
  kind: FilterKindEnum.Browser;
}

interface FeedbackFilter extends AbstractJsonFilter<FEEDBACK_TYPE[]> {
  kind: FilterKindEnum.IncomingFeedback;
}

interface SurveyCompletionFilter extends AbstractJsonFilter<SURVEY_COMPLETION_TYPE> {
  kind: FilterKindEnum.SurveyCompletionKind;
}

interface NPSFilter extends AbstractJsonFilter<NPS_TYPE[]> {
  kind: FilterKindEnum.NPS;
}

interface UserIDFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.UserID;
}

interface KeywordFilter extends AbstractJsonFilter<RadioTextFilterValue> {
  kind: FilterKindEnum.Keyword;
}

interface PageCountFilter extends AbstractJsonFilter<RadioNumberFilterValue> {
  kind: FilterKindEnum.PageCount;
}

interface SessionDurationFilter extends AbstractJsonFilter<RadioDurationFilterValue> {
  kind: FilterKindEnum.SessionDuration;
}

interface ClickedFilter extends AbstractJsonFilter<ClickedType> {
  kind: FilterKindEnum.Clicked;
}

export interface ErrorFilter extends AbstractJsonFilter<ErrorType> {
  kind: FilterKindEnum.Error;
}

export interface FunnelFilter extends AbstractJsonFilter<FunnelType> {
  kind: FilterKindEnum.Funnel;
}

// Currently Unions have limitation on the number of members they can have.
// https://github.com/microsoft/TypeScript/issues/47077
export type FilterType =
  | ClickedFilter
  | ErrorFilter
  | NewReturningUserFilter
  | RageClickFilter
  | UTurnFilter
  | TeamCommentFilter
  | DeviceFilter
  | OSFilter
  | CountryFilter
  | EventFilter
  | GoogleOptimizeFilter
  | OptimizelyFilter
  | UnbounceFilter
  | UserAttributeFilter
  | VisitedURLFilter
  | LandingURLFilter
  | ExitURLFilter
  | ReferrerURLFilter
  | PageURLFilter
  | BrowserFilter
  | FeedbackFilter
  | SurveyCompletionFilter
  | NPSFilter
  | KeywordFilter
  | UserIDFilter
  | PageCountFilter
  | SessionDurationFilter
  | FunnelFilter;

export type UpsellType = 'footer' | FilterKindEnum;

export interface GlobalFilter {
  dateRange?: DateRange;
  filterList: FilterType[];
}

interface AbstractSingleFilterState<T extends FilterKindEnum> {
  kind: T;
  metaData: FilterMetadata;
  isUsed: boolean;
}

interface BasicSingleFilterState
  extends AbstractSingleFilterState<
    Exclude<
      FilterKindEnum,
      [
        FilterKindEnum.Country,
        FilterKindEnum.Event,
        FilterKindEnum.GoogleOptimize,
        FilterKindEnum.Optimizely,
        FilterKindEnum.Unbounce,
        FilterKindEnum.UserAttribute,
      ]
    >
  > {
  config: BasicSingleFilterConfig;
}

export interface LargeListSingleFilterState
  extends AbstractSingleFilterState<FilterKindEnum.Country | FilterKindEnum.Event> {
  config: LargeListSingleFilterConfig;
}

export interface ExperimentSingleFilterState
  extends AbstractSingleFilterState<
    FilterKindEnum.GoogleOptimize | FilterKindEnum.Optimizely | FilterKindEnum.Unbounce
  > {
  config: ExperimentSingleFilterConfig;
}

export interface UserAttributeSingleFilterState
  extends AbstractSingleFilterState<FilterKindEnum.UserAttribute> {
  config: UserAttributeSingleFilterConfig;
}

export type SingleFilterState =
  | BasicSingleFilterState
  | LargeListSingleFilterState
  | ExperimentSingleFilterState
  | UserAttributeSingleFilterState;
